import React from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import ListIcon from '@mui/icons-material/List';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import StoreIcon from '@mui/icons-material/Store';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HubIcon from '@mui/icons-material/Hub';
import PaymentsIcon from '@mui/icons-material/Payments';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import TvIcon from '@mui/icons-material/Tv';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import DownloadIcon from '@mui/icons-material/Download';
import BuildIcon from '@mui/icons-material/Build';
import CategoryIcon from '@mui/icons-material/Category';
import FlagIcon from '@mui/icons-material/Flag';
import { Link, useNavigate } from 'react-router-dom';
import { features } from '../../config/features';
import { visibleGroups } from '../../config/navigation';
import { moduleColor, SUB_ICON } from '../../config/platformModules';
import './sidebar.css';
import { ReactComponent as DigntecLogo } from "../../Assets/images/DigntecLogo.svg";
import { logout } from '../../Redux/Actions/userAction';
import {useDispatch} from 'react-redux';
import { useMediaQuery } from '@mui/material';

// navigation.js 의 icon 이름 → MUI 아이콘
const ICONS = {
    Dashboard: DashboardIcon, TextSnippet: TextSnippetIcon, PersonAdd: PersonAddIcon, DesktopMac: DesktopMacIcon,
    List: ListIcon, Store: StoreIcon, Storefront: StorefrontIcon, MenuBook: MenuBookIcon, EventNote: EventNoteIcon,
    Hub: HubIcon, Payments: PaymentsIcon, MonitorHeart: MonitorHeartIcon, Assignment: AssignmentIcon,
    ReceiptLong: ReceiptLongIcon, RocketLaunch: RocketLaunchIcon, History: HistoryIcon, Settings: SettingsIcon,
    Tv: TvIcon, DeviceHub: DeviceHubIcon, Slideshow: SlideshowIcon, ViewQuilt: ViewQuiltIcon, Download: DownloadIcon, Build: BuildIcon,
    Category: CategoryIcon, Flag: FlagIcon,
};

// 그룹 라벨 = 모듈 대표 색 (platform 아이콘·색 표준, 12.01 R001). 하위 메뉴 아이콘은 회색
function SidebarGroup({ label, children, isMobile, isDrawer, color }) {
    const hasVisible = React.Children.toArray(children).some(c => c);
    if (!hasVisible) return null;
    const labelStyle = {
        fontSize: (isMobile && !isDrawer) ? 9 : 10,
        fontWeight: 700,
        color: color || (isDrawer ? '#5b5b5b' : (isMobile && !isDrawer) ? '#9ca3af' : '#7a7a7a'),
        textTransform: 'uppercase',
        letterSpacing: (isMobile && !isDrawer) ? '0.6px' : '1.2px',
        padding: isDrawer ? '12px 4px 6px' : (isMobile && !isDrawer) ? '12px 2px 4px' : '14px 16px 4px',
        margin: 0,
        display: 'block',
        textAlign: (isMobile && !isDrawer) ? 'center' : 'left',
        lineHeight: 1.2,
    };
    return (
        <>
            <p className={isDrawer ? 'sidebar-section-label sidebar-section-label--drawer' : 'sidebar-section-label'} style={labelStyle}>
                {color && <span aria-hidden style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: color, marginRight: 6, verticalAlign: 'middle' }} />}
                {label}
            </p>
            {children}
        </>
    );
}

function Sidebar({ user, onNavigate }) {
    const isMobile = useMediaQuery('(max-width:600px)');
    const isDrawer = useMediaQuery('(max-width:480px)');
    const dispatch=useDispatch();
    const navigate=useNavigate();
    const logoutHandler=()=>{
        dispatch(logout())
        return navigate('/login')
    }

    const groups = visibleGroups(user?.role, features);
    const textStyle = { fontSize: 15, color: '#B8B8B8', fontFamily: 'Open Sans', display: (isMobile && !isDrawer) ? 'none' : undefined };
    const handleNav = () => { if (onNavigate) onNavigate(); };

    const NavItem = ({ item }) => {
        const Icon = ICONS[item.icon];
        const icon = Icon ? <Icon className='link-a-color' style={{ color: SUB_ICON }} /> : null;
        if (item.external) {
            return (
                <a
                    className="links"
                    data-label={item.label}
                    title={item.title || item.label}
                    aria-label={item.title || item.label}
                    style={{ textDecoration: 'none' }}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleNav}
                >
                    <div className='link-icon'>{icon}</div>
                    <span style={textStyle}>{item.label}</span>
                </a>
            );
        }
        return (
            <Link
                className="links"
                data-label={item.label}
                title={item.label}
                aria-label={item.label}
                style={{ textDecoration: 'none' }}
                onClick={handleNav}
                to={item.path}
            >
                <div className='link-icon'>{icon}</div>
                <span style={textStyle}>{item.label}</span>
            </Link>
        );
    };

    const logoW = isDrawer ? 72 : isMobile ? 46 : 130;
    const upperStyle = isDrawer
      ? { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '6px 10px 10px', minHeight: 0 }
      : isMobile
      ? { display: 'flex', justifyContent: 'center', alignItems: 'center', width: '70px', padding: '6px 0', minHeight: 0 }
      : {};

    return (
        <div className={`sidebar-container${isDrawer ? ' sidebar-container--drawer' : ''}`} style={isMobile && !isDrawer ? { width: '70px' } : isDrawer ? { width: 'min(280px, 92vw)' } : {}}>
            <div className="sidebar-upper-sec" style={upperStyle}>
                <DigntecLogo style={{ width: logoW, height: 'auto', maxHeight: isDrawer ? 44 : isMobile ? 32 : undefined, display: 'block' }} />
            </div>
            <div className="sidebar-middle-sec">
                <div className={`side-bar-box${isDrawer ? ' side-bar-box--drawer' : ''}`}>
                    {groups.map((g) => {
                        const items = g.items.map((item) => <NavItem key={item.path || item.href} item={item} />);
                        return g.label ? (
                            <SidebarGroup key={g.id} label={g.label} color={g.module ? moduleColor(g.module) : undefined} isMobile={isMobile} isDrawer={isDrawer}>{items}</SidebarGroup>
                        ) : (
                            <React.Fragment key={g.id}>{items}</React.Fragment>
                        );
                    })}

                    {/* ── Logout ── */}
                    <div style={{ borderTop: '1px solid #eee', marginTop: 12, paddingTop: 8 }}>
                        <div className="links" data-label="Logout" title="Logout" aria-label="Logout" role="button" tabIndex={0} style={{ cursor: 'pointer' }} onClick={logoutHandler} onKeyDown={(e) => { if (e.key === 'Enter') logoutHandler(); }}>
                            <div className='link-icon'><ExitToAppIcon className='link-a-color' style={{ color: SUB_ICON }} /></div>
                            <span style={textStyle}>Logout</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Sidebar
